/**
 * VoiceChatModule — top-level orchestrator for WebRTC mesh voice chat.
 *
 * Responsibilities:
 *   - Manages peer connections (add/remove based on signaling)
 *   - Owns the local mic `VoiceCapture`
 *   - Owns the `VoiceSpatialRouter` for remote-voice 3D positioning
 *   - Forwards inbound signaling messages to the matching peer
 *   - Emits lifecycle events ('peer-joined', 'peer-left', 'speaking-changed')
 *
 * The module does NOT depend on the full `IModule` interface — it's a
 * plain class that can be instantiated standalone OR wrapped by an
 * engine-side IModule.
 */

import type { VoiceChatConfig } from './VoiceChatConfig.js';
import { createVoiceChatConfig } from './VoiceChatConfig.js';
import type { IVoiceSignaling } from './IVoiceSignaling.js';
import { VoicePeerConnection } from './VoicePeerConnection.js';
import { VoiceCapture, type VoiceActivityEvent } from './VoiceCapture.js';
import { VoiceSpatialRouter } from './VoiceSpatialRouter.js';

export interface VoiceChatEvents {
  onPeerJoined?: (peerId: string) => void;
  onPeerLeft?: (peerId: string) => void;
  onSpeakingChanged?: (peerId: string, isSpeaking: boolean) => void;
  onControlMessage?: (peerId: string, message: Record<string, unknown>) => void;
}

export class VoiceChatModule {
  readonly ctx: AudioContext;
  readonly config: VoiceChatConfig;
  readonly signaling: IVoiceSignaling;
  readonly capture: VoiceCapture;
  readonly router: VoiceSpatialRouter;

  private _peers = new Map<string, VoicePeerConnection>();
  private _offSignaling: (() => void) | null = null;
  private _disposed = false;
  private _events: VoiceChatEvents = {};
  private _peerSpeakingState = new Map<string, boolean>();

  constructor(
    ctx: AudioContext, signaling: IVoiceSignaling,
    parentBus: AudioNode | null, overrides: Partial<VoiceChatConfig> = {},
  ) {
    this.ctx = ctx;
    this.config = createVoiceChatConfig(overrides);
    this.signaling = signaling;
    this.capture = new VoiceCapture(ctx, this.config);
    this.router = new VoiceSpatialRouter(
      ctx, parentBus ?? undefined,
      { maxDistance: this.config.spatialMaxDistance },
    );
    // Inbound signaling → route to the right peer.
    const off = signaling.onReceive((from, msg) => {
      if (this._disposed) return; // a straggler offer used to mint a zombie RTCPeerConnection
      let peer = this._peers.get(from);
      if (!peer && msg.kind === 'offer') {
        // Remote-initiated connection; we're the answerer.
        peer = this._createPeer(from, /*polite=*/ true);
      }
      // Surface (don't swallow) signal failures — a silent catch here cost a
      // full debugging session: the answerer's setRemoteDescription can fail
      // and the initiator just trickles candidates into the void forever.
      if (peer) peer.handleSignal(msg).catch((e) => console.warn('[VoiceChat] handleSignal failed:', (msg as { kind?: string }).kind, e));
    });
    this._offSignaling = typeof off === 'function' ? off : null;
    // Forward local VAD state via the voice-rt channel.
    this.capture.onVoiceActivity = (ev: VoiceActivityEvent) => {
      for (const peer of this._peers.values()) {
        peer.sendRt({ kind: 'vad', isSpeaking: ev.isSpeaking });
      }
    };
  }

  /** Register event handlers. */
  on(events: VoiceChatEvents): void { this._events = { ...this._events, ...events }; }

  /** Acquire the mic (call from a user gesture on iOS Safari). */
  async acquireMic(): Promise<void> {
    await this.capture.acquire();
    // Retroactively add the mic stream's tracks to any peer connections that
    // were created BEFORE acquireMic() resolved. Without this, peers created
    // during lobby/connection setup (a common ordering — connection happens
    // before the user gesture grants mic permission) would never carry
    // audio: they'd have data channels but no audio sender. addTrack()
    // automatically fires `negotiationneeded` on the peer connection so the
    // sender is propagated via SDP renegotiation.
    const stream = this.capture.stream;
    if (stream) {
      for (const peer of this._peers.values()) {
        // Skip if this peer connection already has an audio slot. A sender
        // with `track === null` (after replaceTrack(null) for mute) reports
        // `track?.kind === undefined`, so we'd erroneously call addTrack
        // again — creating a second audio m-section that fails negotiation.
        // Treat any sender with a null track OR an audio-kind track as
        // "audio slot already exists".
        const hasAudio = peer.pc.getSenders().some(
          s => s.track === null || s.track.kind === 'audio',
        );
        if (hasAudio) continue;
        for (const track of stream.getAudioTracks()) {
          try { peer.pc.addTrack(track, stream); }
          catch (e) { console.warn('[VoiceChat] addTrack on existing peer failed:', e); }
        }
      }
    }
  }

  /**
   * Initiate a connection to a remote peer. This is the "polite vs impolite"
   * assignment: the peer with the SMALLER id is polite (rolls back on collision).
   */
  connectToPeer(remotePeerId: string): VoicePeerConnection {
    if (this._peers.has(remotePeerId)) return this._peers.get(remotePeerId)!;
    if (this._peers.size >= this.config.maxPeers) {
      throw new Error(`voice: maxPeers=${this.config.maxPeers} reached`);
    }
    const polite = this.signaling.localPeerId < remotePeerId;
    return this._createPeer(remotePeerId, polite);
  }

  private _createPeer(remotePeerId: string, polite: boolean): VoicePeerConnection {
    const peer = new VoicePeerConnection(
      remotePeerId, polite, this.config, this.signaling, this.capture.stream,
    );
    peer.onRemoteStream = (p, stream) => {
      this.router.attach(p.remotePeerId, stream);
    };
    peer.onControlMessage = (p, msg) => {
      // Route VAD messages to speaking-changed events.
      if (msg.kind === 'vad' && typeof msg.isSpeaking === 'boolean') {
        const prev = this._peerSpeakingState.get(p.remotePeerId) ?? false;
        if (prev !== msg.isSpeaking) {
          this._peerSpeakingState.set(p.remotePeerId, msg.isSpeaking);
          this._events.onSpeakingChanged?.(p.remotePeerId, msg.isSpeaking);
        }
      }
      this._events.onControlMessage?.(p.remotePeerId, msg);
    };
    peer.onClosed = (p) => {
      this._peers.delete(p.remotePeerId);
      this.router.detach(p.remotePeerId);
      this._peerSpeakingState.delete(p.remotePeerId);
      this._events.onPeerLeft?.(p.remotePeerId);
    };
    this._peers.set(remotePeerId, peer);
    // Apply quality preset once the PC transitions to 'connected'.
    // WHY: `connectionstatechange` fires on EVERY transition into 'connected',
    // including ICE recovery (connected→disconnected→connected). Without a
    // one-shot guard, onPeerJoined re-fires on each recovery, so consumers see
    // spurious "peer joined" events for an already-joined peer. Latch on first
    // connect so the lifecycle event fires exactly once per connection.
    let hasJoined = false;
    peer.pc.addEventListener('connectionstatechange', () => {
      if (peer.pc.connectionState === 'connected' && !hasJoined) {
        hasJoined = true;
        peer.applyQuality(this.config.quality).catch(() => {});
        this._events.onPeerJoined?.(peer.remotePeerId);
      }
    });
    return peer;
  }

  /** Disconnect a specific peer. */
  disconnectPeer(peerId: string): void {
    const peer = this._peers.get(peerId);
    if (peer) peer.close();
  }

  /** Update a remote peer's world position for spatial audio. */
  setPeerPosition(peerId: string, x: number, y: number, z: number): void {
    this.router.setPosition(peerId, x, y, z);
  }

  /** Broadcast a reliable control message to all connected peers. */
  broadcastControl(message: Record<string, unknown>): void {
    for (const peer of this._peers.values()) peer.sendControl(message);
  }

  /** Set local mute state (PTT off / VAD gate closed / user-muted). */
  setMuted(muted: boolean): void {
    this.capture.setMute(muted);
    this.broadcastControl({ kind: 'mute', muted });
  }

  /** PTT helper — down = mic hot, up = muted. */
  setPressToTalk(pressed: boolean): void {
    this.capture.setPressToTalk(pressed);
  }

  /** Currently connected peer ids. */
  get peerIds(): string[] { return Array.from(this._peers.keys()); }

  /** Dispose all peers, mic, and audio graph. */
  dispose(): void {
    this._disposed = true;
    this._offSignaling?.();
    this._offSignaling = null;
    for (const peer of Array.from(this._peers.values())) peer.close();
    this._peers.clear();
    this.capture.dispose();
    this.router.dispose();
  }
}
